import { useState, useEffect } from 'react';
import { adminLogsService, type ActivityLog, type SystemLog, type ActivityFilterOptions, type SystemLogFilterOptions } from '../../lib/api/logs';

type TabType = 'activity' | 'system';

export default function LogsPage() {
    const [activeTab, setActiveTab] = useState<TabType>('activity');

    return (
        <div className="min-h-screen bg-neutral-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-neutral-900">Logs</h1>
                    <p className="text-neutral-500 mt-1">View and manage activity and system logs</p>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm border border-neutral-200 w-fit">
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'activity'
                                ? 'bg-accent-primary text-white shadow-sm'
                                : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                            }`}
                    >
                        Activity Logs
                    </button>
                    <button
                        onClick={() => setActiveTab('system')}
                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'system'
                                ? 'bg-accent-primary text-white shadow-sm'
                                : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                            }`}
                    >
                        System Logs
                    </button>
                </div>

                {/* Content */}
                {activeTab === 'activity' ? <ActivityLogsTab /> : <SystemLogsTab />}
            </div>
        </div>
    );
}

// ===========================================================================
// Activity Logs Tab
// ===========================================================================

function ActivityLogsTab() {
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState<ActivityFilterOptions>({
        limit: 50,
        offset: 0,
        order: 'desc',
    });
    const [categories, setCategories] = useState<string[]>([]);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        loadLogs();
    }, [filters]);

    const loadCategories = async () => {
        try {
            const res = await adminLogsService.getActivityCategories();
            if (res.success) {
                setCategories(res.data.categories);
            }
        } catch (error) {
            console.error('Failed to load categories', error);
        }
    };

    const loadLogs = async () => {
        setIsLoading(true);
        try {
            const res = await adminLogsService.getActivityLogs(filters);
            if (res.success) {
                setLogs(res.data.logs);
                setTotal(res.data.pagination.total);
            }
        } catch (error) {
            console.error('Failed to load activity logs', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (key: keyof ActivityFilterOptions, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value || undefined,
            offset: 0, // Reset pagination
        }));
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getCategoryBadgeColor = (category: string) => {
        switch (category) {
            case 'security':
                return 'bg-purple-100 text-purple-700';
            case 'payment':
            case 'subscription':
                return 'bg-green-100 text-green-700';
            case 'channel':
                return 'bg-blue-100 text-blue-700';
            case 'board':
                return 'bg-amber-100 text-amber-700';
            case 'user':
                return 'bg-indigo-100 text-indigo-700';
            default:
                return 'bg-neutral-100 text-neutral-600';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success':
                return 'bg-success-500';
            case 'failure':
                return 'bg-error-500';
            case 'warning':
                return 'bg-amber-500';
            default:
                return 'bg-neutral-400';
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b border-neutral-200 flex flex-wrap gap-3 items-center">
                <input
                    type="text"
                    placeholder="Search actions..."
                    className="px-3 py-2 border border-neutral-200 rounded-lg text-sm w-64 focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                />
                <select
                    className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white"
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
                <select
                    className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                    <option value="">All Status</option>
                    <option value="success">Success</option>
                    <option value="failure">Failure</option>
                    <option value="warning">Warning</option>
                </select>
                <div className="flex-1" />
                <span className="text-sm text-neutral-500">{total} logs</span>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-primary border-t-transparent mx-auto"></div>
                </div>
            ) : logs.length === 0 ? (
                <div className="p-12 text-center text-neutral-500">
                    No activity logs found
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-neutral-50 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            <tr>
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">Action</th>
                                <th className="px-4 py-3">Category</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">IP</th>
                                <th className="px-4 py-3">Time</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                            {logs.map(log => (
                                <tr key={log.id} className="hover:bg-neutral-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="text-sm font-medium text-neutral-900">{log.user?.email || log.userId}</div>
                                        <div className="text-xs text-neutral-400">{log.user?.name}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-sm text-neutral-900">{log.action}</div>
                                        <div className="text-xs text-neutral-400 font-mono">{log.eventType}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeColor(log.category)}`}>
                                            {log.category}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2 h-2 rounded-full ${getStatusColor(log.status)}`} />
                                            <span className="text-sm text-neutral-600 capitalize">{log.status}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-neutral-500 font-mono">
                                        {log.ipAddress || '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-neutral-500 whitespace-nowrap">
                                        {formatDate(log.createdAt)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {total > 50 && (
                <div className="p-4 border-t border-neutral-200 flex items-center justify-between">
                    <button
                        disabled={filters.offset === 0}
                        onClick={() => setFilters(prev => ({ ...prev, offset: Math.max(0, (prev.offset || 0) - 50) }))}
                        className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-neutral-500">
                        Showing {(filters.offset || 0) + 1} - {Math.min((filters.offset || 0) + 50, total)} of {total}
                    </span>
                    <button
                        disabled={(filters.offset || 0) + 50 >= total}
                        onClick={() => setFilters(prev => ({ ...prev, offset: (prev.offset || 0) + 50 }))}
                        className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

// ===========================================================================
// System Logs Tab
// ===========================================================================

function SystemLogsTab() {
    const [logs, setLogs] = useState<SystemLog[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState<SystemLogFilterOptions>({
        limit: 100,
        offset: 0,
        order: 'desc',
    });
    const [services, setServices] = useState<string[]>([]);
    const [stats, setStats] = useState<{
        totalErrors: number;
        byErrorCode: Array<{ errorCode: string; count: number }>;
        levelDistribution: Record<string, number>;
    } | null>(null);
    const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

    useEffect(() => {
        loadServices();
        loadStats();
    }, []);

    useEffect(() => {
        loadLogs();
    }, [filters]);

    const loadServices = async () => {
        try {
            const res = await adminLogsService.getSystemServices();
            if (res.success) {
                setServices(res.data.services);
            }
        } catch (error) {
            console.error('Failed to load services', error);
        }
    };

    const loadStats = async () => {
        try {
            const res = await adminLogsService.getSystemLogStats();
            if (res.success) {
                setStats({
                    totalErrors: res.data.errors.totalErrors,
                    byErrorCode: res.data.errors.byErrorCode,
                    levelDistribution: res.data.levelDistribution,
                });
            }
        } catch (error) {
            console.error('Failed to load stats', error);
        }
    };

    const loadLogs = async () => {
        setIsLoading(true);
        try {
            const res = await adminLogsService.getSystemLogs(filters);
            if (res.success) {
                setLogs(res.data.logs);
                setTotal(res.data.pagination.total);
            }
        } catch (error) {
            console.error('Failed to load system logs', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (key: keyof SystemLogFilterOptions, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value || undefined,
            offset: 0,
        }));
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    const getLevelBadgeColor = (level: string) => {
        switch (level) {
            case 'error':
                return 'bg-error-100 text-error-700';
            case 'warn':
                return 'bg-amber-100 text-amber-700';
            case 'info':
                return 'bg-blue-100 text-blue-700';
            case 'debug':
                return 'bg-neutral-100 text-neutral-600';
            default:
                return 'bg-neutral-100 text-neutral-600';
        }
    };

    return (
        <div className="space-y-6">
            {/* Stats Row */}
            {stats && (
                <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-neutral-200">
                        <p className="text-sm text-neutral-500">Total Errors</p>
                        <p className="text-2xl font-bold text-error-600">{stats.totalErrors}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-neutral-200">
                        <p className="text-sm text-neutral-500">Warnings</p>
                        <p className="text-2xl font-bold text-amber-600">{stats.levelDistribution['warn'] || 0}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-neutral-200">
                        <p className="text-sm text-neutral-500">Info Logs</p>
                        <p className="text-2xl font-bold text-blue-600">{stats.levelDistribution['info'] || 0}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-neutral-200">
                        <p className="text-sm text-neutral-500">Debug Logs</p>
                        <p className="text-2xl font-bold text-neutral-600">{stats.levelDistribution['debug'] || 0}</p>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
                {/* Filters */}
                <div className="p-4 border-b border-neutral-200 flex flex-wrap gap-3 items-center">
                    <input
                        type="text"
                        placeholder="Search messages..."
                        className="px-3 py-2 border border-neutral-200 rounded-lg text-sm w-64 focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                    <select
                        className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white"
                        onChange={(e) => handleFilterChange('level', e.target.value)}
                    >
                        <option value="">All Levels</option>
                        <option value="error">Error</option>
                        <option value="warn">Warning</option>
                        <option value="info">Info</option>
                        <option value="debug">Debug</option>
                    </select>
                    <select
                        className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white"
                        onChange={(e) => handleFilterChange('service', e.target.value)}
                    >
                        <option value="">All Services</option>
                        {services.map(svc => (
                            <option key={svc} value={svc}>{svc}</option>
                        ))}
                    </select>
                    <div className="flex-1" />
                    <span className="text-sm text-neutral-500">{total} logs</span>
                </div>

                {/* Table */}
                {isLoading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-primary border-t-transparent mx-auto"></div>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="p-12 text-center text-neutral-500">
                        No system logs found
                    </div>
                ) : (
                    <div className="divide-y divide-neutral-100">
                        {logs.map(log => (
                            <div key={log.id}>
                                <div
                                    className="px-4 py-3 hover:bg-neutral-50 cursor-pointer transition-colors flex items-start gap-3"
                                    onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                                >
                                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium uppercase ${getLevelBadgeColor(log.level)}`}>
                                        {log.level}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-neutral-900 truncate">{log.message}</p>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-neutral-400">
                                            <span>{log.service}</span>
                                            {log.source && <span className="font-mono">{log.source}</span>}
                                            {log.errorCode && <span className="text-error-500">{log.errorCode}</span>}
                                        </div>
                                    </div>
                                    <span className="text-xs text-neutral-400 whitespace-nowrap">{formatDate(log.createdAt)}</span>
                                </div>

                                {/* Expanded Details */}
                                {expandedLogId === log.id && (
                                    <div className="px-4 py-4 bg-neutral-50 border-t border-neutral-100">
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <p className="text-xs font-medium text-neutral-500 mb-1">Request ID</p>
                                                <p className="text-sm font-mono text-neutral-700">{log.requestId || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-neutral-500 mb-1">User ID</p>
                                                <p className="text-sm font-mono text-neutral-700">{log.userId || '-'}</p>
                                            </div>
                                        </div>
                                        {log.errorStack && (
                                            <div className="mb-4">
                                                <p className="text-xs font-medium text-neutral-500 mb-1">Stack Trace</p>
                                                <pre className="text-xs bg-neutral-900 text-neutral-100 p-3 rounded-lg overflow-x-auto max-h-48">
                                                    {log.errorStack}
                                                </pre>
                                            </div>
                                        )}
                                        {log.context && Object.keys(log.context).length > 0 && (
                                            <div>
                                                <p className="text-xs font-medium text-neutral-500 mb-1">Context</p>
                                                <pre className="text-xs bg-neutral-900 text-neutral-100 p-3 rounded-lg overflow-x-auto max-h-48">
                                                    {JSON.stringify(log.context, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {total > 100 && (
                    <div className="p-4 border-t border-neutral-200 flex items-center justify-between">
                        <button
                            disabled={filters.offset === 0}
                            onClick={() => setFilters(prev => ({ ...prev, offset: Math.max(0, (prev.offset || 0) - 100) }))}
                            className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-neutral-500">
                            Showing {(filters.offset || 0) + 1} - {Math.min((filters.offset || 0) + 100, total)} of {total}
                        </span>
                        <button
                            disabled={(filters.offset || 0) + 100 >= total}
                            onClick={() => setFilters(prev => ({ ...prev, offset: (prev.offset || 0) + 100 }))}
                            className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
